import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ===== Trigger / Action shapes =====
export type TriggerType =
  | 'item_created'       // fires when a new item appears on a board
  | 'status_changes_to'  // fires when an item's status column changes to X
  | 'person_assigned'    // fires when an item's person column changes to X
  | 'date_arrives';      // fires daily for items where date == today - leadDays

export interface Trigger {
  type: TriggerType;
  boardId?: string;                // which board (undefined = any)
  status?: string;                 // for status_changes_to
  personId?: string;               // for person_assigned (undefined = anyone)
  leadDays?: number;               // for date_arrives (0 = today, 1 = one day before)
}

export type ActionType =
  | 'notify_person'      // create a Notification for a user
  | 'set_status'         // set the status column of the item
  | 'move_to_group'      // move item to another group
  | 'post_update'        // add an Update on the item
  | 'create_item';       // create a brand new item

export interface Action {
  type: ActionType;
  personId?: string;           // notify_person
  message?: string;            // notify_person, post_update
  status?: string;             // set_status
  groupId?: string;            // move_to_group or create_item
  boardId?: string;            // create_item
  nameTemplate?: string;       // create_item (supports {item_name} placeholder)
}

export interface Automation {
  id: string;
  name: string;
  boardId: string;            // anchor board (the automation lives on this board)
  isEnabled: boolean;
  trigger: Trigger;
  actions: Action[];
  runCount: number;
  lastRunAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AutomationRun {
  id: string;
  automationId: string;
  status: 'success' | 'failed' | 'skipped';
  input?: unknown;
  output?: unknown;
  error?: string;
  createdAt: string;
}

const uid = (p = 'id') => `${p}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
const now = () => new Date().toISOString();

// ===== Seed =====
function makeSeed(): { automations: Automation[]; runs: AutomationRun[] } {
  return {
    automations: [
      {
        id: 'auto_launch_done',
        name: 'Celebrate when a launch task is done',
        boardId: 'bd_marketing',
        isEnabled: true,
        trigger: { type: 'status_changes_to', boardId: 'bd_marketing', status: 'done' },
        actions: [
          { type: 'notify_person', personId: 'u_alona', message: ' A Marketing item just went to Done!' },
        ],
        runCount: 3,
        lastRunAt: now(),
        createdAt: now(),
        updatedAt: now(),
      },
      {
        id: 'auto_new_bug',
        name: 'When a new item is added, assign to Yoni',
        boardId: 'bd_sprint',
        isEnabled: false,
        trigger: { type: 'item_created', boardId: 'bd_sprint' },
        actions: [
          { type: 'post_update', message: 'Auto-triaged. @u_yoni take a look when you can.' },
        ],
        runCount: 0,
        createdAt: now(),
        updatedAt: now(),
      },
      {
        id: 'auto_due_soon',
        name: 'Notify owners 1 day before due',
        boardId: 'bd_roadmap',
        isEnabled: true,
        trigger: { type: 'date_arrives', boardId: 'bd_roadmap', leadDays: 1 },
        actions: [
          { type: 'notify_person', personId: 'u_alona', message: 'Due tomorrow — stay on top of it.' },
        ],
        runCount: 0,
        createdAt: now(),
        updatedAt: now(),
      },
    ],
    runs: [
      { id: uid('run'), automationId: 'auto_launch_done', status: 'success', createdAt: now(), output: 'Notified Alona' },
    ],
  };
}

interface AutomationState {
  automations: Automation[];
  runs: AutomationRun[];

  createAutomation: (boardId: string, name: string, trigger: Trigger, actions: Action[]) => string;
  updateAutomation: (id: string, patch: Partial<Omit<Automation, 'id'>>) => void;
  toggleAutomation: (id: string) => void;
  deleteAutomation: (id: string) => void;
  recordRun: (automationId: string, status: AutomationRun['status'], detail?: { output?: unknown; error?: string }) => void;
  clearRuns: (automationId?: string) => void;
  resetToSeed: () => void;
}

export const useAutomationStore = create<AutomationState>()(
  persist(
    (set) => ({
      ...makeSeed(),

      createAutomation: (boardId, name, trigger, actions) => {
        const id = uid('auto');
        set((s) => ({
          automations: [...s.automations, {
            id, boardId, name,
            isEnabled: true,
            trigger, actions,
            runCount: 0,
            createdAt: now(), updatedAt: now(),
          }],
        }));
        return id;
      },
      updateAutomation: (id, patch) => set((s) => ({
        automations: s.automations.map((a) => a.id === id ? { ...a, ...patch, updatedAt: now() } : a),
      })),
      toggleAutomation: (id) => set((s) => ({
        automations: s.automations.map((a) => a.id === id ? { ...a, isEnabled: !a.isEnabled, updatedAt: now() } : a),
      })),
      deleteAutomation: (id) => set((s) => ({
        automations: s.automations.filter((a) => a.id !== id),
        runs: s.runs.filter((r) => r.automationId !== id),
      })),
      recordRun: (automationId, status, detail) => set((s) => ({
        runs: [{
          id: uid('run'), automationId, status,
          output: detail?.output, error: detail?.error,
          createdAt: now(),
        }, ...s.runs].slice(0, 200), // keep only 200 most recent runs
        automations: s.automations.map((a) => a.id === automationId
          ? { ...a, runCount: a.runCount + 1, lastRunAt: now() }
          : a,
        ),
      })),
      clearRuns: (automationId) => set((s) => ({
        runs: automationId ? s.runs.filter((r) => r.automationId !== automationId) : [],
      })),
      resetToSeed: () => set(makeSeed()),
    }),
    { name: 'myday-automations', version: 1 },
  ),
);

// ===== Selectors =====
export const selectAutomationsForBoard = (boardId: string) => (s: AutomationState) =>
  s.automations.filter((a) => a.boardId === boardId);

export const selectRunsForAutomation = (automationId: string) => (s: AutomationState) =>
  s.runs.filter((r) => r.automationId === automationId).slice(0, 50);

// ===== Catalog (for the builder UI) =====
export const TRIGGER_CATALOG: { type: TriggerType; label: string; describe: (t: Trigger) => string }[] = [
  { type: 'item_created',      label: 'When an item is created',       describe: () => 'a new item is added to this board' },
  { type: 'status_changes_to', label: 'When status changes to…',       describe: (t) => `status becomes "${t.status ?? 'Done'}"` },
  { type: 'person_assigned',   label: 'When someone is assigned',      describe: (t) => t.personId ? `a person is assigned` : `anyone is assigned` },
  { type: 'date_arrives',      label: 'When a due date arrives',       describe: (t) => t.leadDays ? `${t.leadDays} day(s) before the due date` : 'on the due date' },
];

export const ACTION_CATALOG: { type: ActionType; label: string; describe: (a: Action) => string }[] = [
  { type: 'notify_person', label: 'Notify a teammate',      describe: (a) => `notify ${a.personId ?? 'someone'}` },
  { type: 'set_status',    label: 'Set status',             describe: (a) => `set status to "${a.status ?? 'Done'}"` },
  { type: 'move_to_group', label: 'Move to a group',        describe: () => `move the item to another group` },
  { type: 'post_update',   label: 'Post an update',         describe: () => `post an update on the item` },
  { type: 'create_item',   label: 'Create a new item',      describe: () => `create a new item on a board` },
];
